'use client';

import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import { formatDateTime } from '@/lib/utils';

export interface LocalDateTimeProps {
  iso: string;
  locale: string;
  className?: string;
  /** Formatter with the `(iso, locale, timeZone?)` signature. Defaults to {@link formatDateTime}. */
  format?: (iso: string, locale: string, timeZone?: string) => string;
}

/**
 * Hydration-safe localized datetime — use instead of calling `formatDateTime`
 * directly in a client component's render. The zone comes from
 * `useHydratedTimeZone`: the pinned `DisplayTimeZoneProvider` zone when one is
 * set (event pages), else UTC before mount (cross-client determinism) and the
 * viewer's local timezone after; `suppressHydrationWarning` tolerates
 * Node-vs-browser ICU string differences that otherwise throw React #418.
 */
export function LocalDateTime({ iso, locale, className, format }: LocalDateTimeProps) {
  const timeZone = useHydratedTimeZone();
  const fmt = format ?? formatDateTime;
  return (
    <time dateTime={iso} className={className} suppressHydrationWarning>
      {fmt(iso, locale, timeZone)}
    </time>
  );
}
